import { useCallback, useRef } from 'react';

const routeModules: Record<string, () => Promise<unknown>> = {
  '/marcas': () => import('@/pages/for-brands'),
  '/para-creadores': () => import('@/pages/for-creators'),
  '/academia-ugc': () => import('@/pages/academy'),
  '/creadores': () => import('@/pages/creators-list'),
  '/podcast': () => import('@/pages/podcast'),
  '/eventos': () => import('@/pages/events'),
  '/sobre-nosotros': () => import('@/pages/about'),
  '/contacto': () => import('@/pages/contact'),
  '/asesorias': () => import('@/pages/asesorias'),
  '/portfolio': () => import('@/pages/portfolio'),
  '/ugc': () => import('@/pages/ugc'),
  '/login': () => import('@/pages/login'),
  '/blog': () => import('@/pages/blog'),
  '/urits': () => import('@/pages/uri-portfolio'),
};

const prefetchedRoutes = new Set<string>();

export function useRoutePrefetch() {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const prefetchRoute = useCallback((path: string) => {
    if (prefetchedRoutes.has(path)) return;

    const moduleLoader = routeModules[path];
    if (moduleLoader) {
      prefetchedRoutes.add(path);
      moduleLoader().catch(() => {
        prefetchedRoutes.delete(path);
      });
    }
  }, []);

  const handleMouseEnter = useCallback((path: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      prefetchRoute(path);
    }, 100);
  }, [prefetchRoute]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return {
    prefetchRoute,
    handleMouseEnter,
    handleMouseLeave,
    getPrefetchProps: (path: string) => ({
      onMouseEnter: () => handleMouseEnter(path),
      onMouseLeave: handleMouseLeave,
      onFocus: () => prefetchRoute(path),
    }),
  };
}

export function prefetchCriticalRoutes() {
  if (typeof window === 'undefined') return;

  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (connection?.saveData) return;
  if (connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g') return;

  setTimeout(() => {
    ['/academia-ugc', '/marcas', '/portfolio', '/urits', '/contacto', '/para-creadores'].forEach(path => {
      const moduleLoader = routeModules[path];
      if (moduleLoader && !prefetchedRoutes.has(path)) {
        prefetchedRoutes.add(path);
        moduleLoader().catch(() => prefetchedRoutes.delete(path));
      }
    });
  }, 3000);
}
